import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, defer } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { estimateBodySize } from './estimate-body-size.util';
import { toLogLabel } from './log-label.util';
import { PerfChannel } from './models/perf-suggestion.model';
import { PerfAdvisorService } from './perf-advisor.service';
import { logPerf } from './perf-logger.util';
import { isSlow } from './perf-thresholds.const';

const CONTENT_LENGTH_HEADER = 'Content-Length';

@Injectable()
export class HttpTimingInterceptor implements HttpInterceptor {
  private readonly advisor = inject(PerfAdvisorService);

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    // defer: count the request when it is actually subscribed to, so every
    // started request is balanced by the finalize below.
    return defer(() => {
      const startMs = performance.now();
      this.advisor.requestStarted();
      let status = 0;
      let headerSizeBytes: number | undefined;
      let responseBody: unknown;

      return next.handle(request).pipe(
        tap({
          next: (event) => {
            if (event instanceof HttpResponse) {
              status = event.status;
              const length = Number(event.headers.get(CONTENT_LENGTH_HEADER));
              headerSizeBytes = length > 0 ? length : undefined;
              responseBody = event.body;
            }
          },
          error: (error: unknown) => {
            if (error instanceof HttpErrorResponse) {
              status = error.status;
            }
          },
        }),
        finalize(() => {
          const endMs = performance.now();
          // Prefer the header; only pay for estimating from the body when the
          // request is slow enough that the size will actually be used.
          const sizeBytes =
            headerSizeBytes ??
            (isSlow(PerfChannel.HTTP, endMs - startMs)
              ? estimateBodySize(responseBody)
              : undefined);
          // The full URL stays in the sample (duplicate detection needs the values);
          // only a value-free label is ever logged.
          const label = `${request.method} ${toLogLabel(request.urlWithParams)}`;
          const suggestions = this.advisor.recordHttp({
            method: request.method,
            url: request.urlWithParams,
            startMs,
            endMs,
            status,
            sizeBytes,
          });

          logPerf(PerfChannel.HTTP, label, endMs - startMs, suggestions);
        })
      );
    });
  }
}
